import { useState } from 'react';
import { Link } from 'react-router-dom';
import { NEED_LABEL, SOURCE_LABEL, isInboundNeed, isNeedRead, isToldUsNeed, isUnmatchedNeed, markInboundRead, markToldUsRead, type NeedRow } from '@/lib/people-data';
import { when } from '@/lib/format';
import s from '@/pages/console/console.module.css';

/**
 * The Needs list's rows. Every free-text thing people typed, one row each,
 * with the keyword tag as a suggestion. The rows that can be marked read:
 *   inbound_email, inbound_sms  a message on an account (pv-auth #336):
 *                               Mark read here, and Reply, which opens the
 *                               person's page with the composer set to answer
 *                               that message.
 *   inbound_unmatched           a GHL contact with no account: a link to the
 *                               thread in GHL and no reply button, because
 *                               there is no person here to send to.
 *   seventh_check_answer, improve, evening_note
 *                               what they told us (#334, #326): Mark read
 *                               through the feedback endpoint (#337).
 * A row with no read state (a reflection, a ticket) is never "read": nothing
 * about it says it was dealt with, so it stays in front.
 *
 * The rows the server (or the page) already marked read are folded under one
 * "Read" disclosure, closed, so the unread ones stay in front. A row marked
 * read here moves into the fold on the server's answer.
 */
export function NeedRows({ rows, onRead }: { rows: NeedRow[]; onRead?: (row: NeedRow) => void }) {
  const [readIds, setReadIds] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const keyOf = (r: NeedRow): string | null => (typeof r.context.messageId === 'string' ? r.context.messageId : r.id ?? null);
  const readHere = (r: NeedRow) => { const k = keyOf(r); return !!k && readIds.has(k); };

  const mark = async (r: NeedRow) => {
    const k = keyOf(r);
    if (!r.userId || !k) return;
    setBusy(k);
    setError(null);
    try {
      if (isInboundNeed(r)) await markInboundRead(r.userId, k);
      else await markToldUsRead(r.userId, k);
      setReadIds((cur) => new Set(cur).add(k));
      onRead?.(r);
    } catch (err) {
      setError(`Could not mark it read: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const unreadRows = rows.filter((r) => !isNeedRead(r) && !readHere(r));
  const readRows = rows.filter((r) => isNeedRead(r) || readHere(r));

  const row = (r: NeedRow, i: number) => {
    const messageId = typeof r.context.messageId === 'string' ? r.context.messageId : null;
    const ghlUrl = typeof r.context.ghlUrl === 'string' ? r.context.ghlUrl : null;
    const inbound = isInboundNeed(r) && !!messageId;
    const toldUs = isToldUsNeed(r);
    const unmatched = isUnmatchedNeed(r);
    const markable = inbound || toldUs;
    const read = isNeedRead(r) || readHere(r);
    const k = keyOf(r);
    const flagged = r.context.flagged === true;
    return (
      <div key={`${r.at}-${i}`} className={`${s.toldRow} ${markable && !read ? s.toldRowUnread : ''} ${flagged ? s.toldRowFlag : ''}`} data-need-source={r.source} data-unread={markable ? (read ? '0' : '1') : undefined} data-flagged={flagged ? '1' : undefined}>
        <span className={s.jTime}>{when(r.at)}</span>
        <span>
          <span className={s.jKind}>{SOURCE_LABEL[r.source] ?? r.source}</span>
          {markable && <> <span className={`${s.pill} ${read ? '' : s.pill_unread}`}>{read ? 'read' : 'unread'}</span></>}
          {flagged && <> <span className={`${s.pill} ${s.pill_flag}`}>flagged</span></>}
          <div className={s.sub}>{r.userId ? <Link to={`/people/${r.userId}`}>{r.name || r.email || 'account'}</Link> : (r.email ?? 'no account')}</div>
          {read && r.readBy && <div className={s.sub}>read by {r.readBy}</div>}
        </span>
        <span className={s.toldText}>{r.text}</span>
        <span className={`${s.pill} ${s[`pill_${r.tag ?? 'untagged'}`]}`}>{r.tag ? `${NEED_LABEL[r.tag]}?` : 'untagged'}</span>
        {(markable || unmatched) && (
          <span className={s.toldActions}>
            {markable && !read && <button type="button" className={s.btnGhost} disabled={busy === k} onClick={() => void mark(r)} data-action="mark-read">{busy === k ? 'Marking' : 'Mark read'}</button>}
            {inbound && <Link className={s.btn} to={`/people/${r.userId}?reply=${encodeURIComponent(messageId!)}`} data-action="reply">Reply</Link>}
            {toldUs && <Link className={s.btnGhost} to={`/people/${r.userId}#told-us`} data-action="open-person">On their page</Link>}
            {unmatched && (ghlUrl ? <a className={s.linkA} href={ghlUrl} target="_blank" rel="noreferrer" data-action="ghl-thread">Open the thread in GHL</a> : <span className={s.missing}>no account and no GHL link</span>)}
            {unmatched && <span className={s.sub}>no account to reply to from here</span>}
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      {error && <div className={s.error}>{error}</div>}
      {unreadRows.map(row)}
      {readRows.length > 0 && (
        <details className={s.readFold} data-panel="read-fold">
          <summary>Read · {readRows.length} {readRows.length === 1 ? 'row' : 'rows'} already dealt with</summary>
          <div className={s.readFoldBody}>{readRows.map(row)}</div>
        </details>
      )}
    </>
  );
}
